import { pathOr } from 'ramda';
import RBush from 'rbush';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EAsyncStatus } from '../../../../common/types/asyncStatus';
import { IInfrastructureFeaturesMap, IInfrastructureRBushItem } from '../../../../common/types/infrastructure';
import { IApplicationState } from '../../../../common/types/redux';
import { geoJsonFeatureToInfrastructureRBushItem } from '../../../../common/utils/features';
import { getInfrastructurePointsTilesToUpdate } from '../../../../common/utils/tiles';
import { onYmapsDebug } from '../../../../common/utils/ymapsModules/ymapsModulesDebug';
import { setVisibleFeaturesCount } from '../../../actions/infrastructure';
import {
  selectActiveInfrastructureFeatures,
  selectActiveInfrastructureFeaturesCount,
  selectActiveType,
  selectFeaturesStatus,
  selectIsWebGlRendering,
  selectLargePinsZoom,
  selectMapBounds,
  selectMinZoom,
} from '../../../selectors';
import { TMapBounds } from '../../../types/mapBounds';

interface IUseInfrastructureLayerParams {
  ymaps: YMaps.IYMaps;
  map: YMaps.Map;
  enabled: boolean;
}

export function useInfrastructureLayer({ ymaps, map, enabled }: IUseInfrastructureLayerParams): void {
  const dispatch = useDispatch();
  const rbushRef = React.useRef(new RBush<IInfrastructureRBushItem>());
  const mapBounds = useSelector<IApplicationState, TMapBounds>(selectMapBounds);
  const { bounds, zoom } = mapBounds;
  const activeType = useSelector(selectActiveType);
  const minZoom = useSelector(selectMinZoom);
  const largePinsZoom = useSelector(selectLargePinsZoom);
  const isWebGlRendering = useSelector(selectIsWebGlRendering);
  const status = useSelector(selectFeaturesStatus);
  const activeFeaturesCount = useSelector(selectActiveInfrastructureFeaturesCount);

  // Lazy init: создаём слои только когда фича включена. На !enabled мы не должны
  // даже конструировать объекты, иначе hook на мобилке, где он раньше не вызывался,
  // начинает выполнять лишнюю работу.
  const infrastructurePointsLayerRef = React.useRef<YMaps.InfrastructurePointsLayer | null>(null);
  const infrastructureHTMLPointsLayerRef = React.useRef<YMaps.InfrastructureHTMLPointsLayer | null>(null);

  if (enabled && !infrastructurePointsLayerRef.current) {
    infrastructurePointsLayerRef.current = new ymaps.InfrastructurePointsLayer({
      map,
      rbush: rbushRef.current,
    });
  }

  if (enabled && !infrastructureHTMLPointsLayerRef.current) {
    infrastructureHTMLPointsLayerRef.current = new ymaps.InfrastructureHTMLPointsLayer({
      map,
      rbush: rbushRef.current,
      minZoom,
      largePinsZoom,
    });
  }

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const infrastructurePointsLayer = infrastructurePointsLayerRef.current;
    const infrastructureHTMLPointsLayer = infrastructureHTMLPointsLayerRef.current;

    if (!infrastructurePointsLayer || !infrastructureHTMLPointsLayer) {
      return;
    }

    if (isWebGlRendering) {
      map.layers.add(infrastructurePointsLayer.layer);
    } else {
      map.geoObjects.add(infrastructureHTMLPointsLayer.objectManager);
    }

    return () => {
      if (isWebGlRendering) {
        map.layers.remove(infrastructurePointsLayer.layer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const webGlInfrastructureFeatures = useSelector<IApplicationState, IInfrastructureFeaturesMap>(state => {
    if (activeType === undefined) {
      return {};
    }

    return pathOr<IInfrastructureFeaturesMap>({}, [activeType.id, 'features'], state.map.infrastructure.data);
  });

  const webGlLoadedFeaturesRef = React.useRef<IInfrastructureFeaturesMap>({});

  React.useEffect(() => {
    if (!enabled || !isWebGlRendering) {
      return;
    }
    rbushRef.current.clear();
    webGlLoadedFeaturesRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, activeType]);

  React.useEffect(() => {
    if (!enabled || !isWebGlRendering) {
      return;
    }

    const rbushItems = Object.values(webGlInfrastructureFeatures)
      .filter(({ id }) => !webGlLoadedFeaturesRef.current[id])
      .map(geoJsonFeatureToInfrastructureRBushItem);

    if (rbushItems.length === 0) {
      if (Object.keys(webGlLoadedFeaturesRef.current).length === 0) {
        infrastructurePointsLayerRef.current?.layer.update();
      }

      return;
    }

    rbushRef.current.load(rbushItems);

    const featuresWereLoaded = Object.keys(webGlLoadedFeaturesRef.current).length > 0;
    webGlLoadedFeaturesRef.current = webGlInfrastructureFeatures;

    if (!featuresWereLoaded) {
      infrastructurePointsLayerRef.current?.layer.update();

      return;
    }

    onYmapsDebug(() => console.time('getInfrastructurePointsTilesToUpdate'));
    const tilesToUpdate = getInfrastructurePointsTilesToUpdate({ rbushItems, bounds, zoom });
    onYmapsDebug(() => console.timeEnd('getInfrastructurePointsTilesToUpdate'));

    onYmapsDebug(() => console.time('infrastructurePointsLayer update'));
    if (tilesToUpdate.length > 0) {
      infrastructurePointsLayerRef.current?.layer.update(tilesToUpdate);
    }
    onYmapsDebug(() => console.timeEnd('infrastructurePointsLayer update'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, webGlInfrastructureFeatures, zoom, bounds]);

  const htmlInfrastructureFeatures = useSelector(selectActiveInfrastructureFeatures);
  const htmlLoadedFeaturesRef = React.useRef<IInfrastructureFeaturesMap>({});
  const prevZoomRef = React.useRef<number>(zoom);

  React.useEffect(() => {
    if (!enabled || isWebGlRendering) {
      return;
    }
    rbushRef.current.clear();
    htmlLoadedFeaturesRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, activeType]);

  React.useEffect(() => {
    if (!enabled || isWebGlRendering) {
      return;
    }

    const rbushItems = Object.values(htmlInfrastructureFeatures)
      .filter(({ id }) => !htmlLoadedFeaturesRef.current[id])
      .map(geoJsonFeatureToInfrastructureRBushItem);

    if (rbushItems.length === 0) {
      if (Object.keys(htmlLoadedFeaturesRef.current).length === 0) {
        infrastructureHTMLPointsLayerRef.current?.clear();
      }

      return;
    }

    rbushRef.current.load(rbushItems);

    const featuresWereLoaded = Object.keys(htmlLoadedFeaturesRef.current).length > 0;
    htmlLoadedFeaturesRef.current = htmlInfrastructureFeatures;

    if (!featuresWereLoaded) {
      infrastructureHTMLPointsLayerRef.current?.drawPins();

      return;
    }

    onYmapsDebug(() => console.time('getInfrastructurePointsTilesToUpdate'));
    const tilesToUpdate = getInfrastructurePointsTilesToUpdate({ rbushItems, bounds, zoom });
    onYmapsDebug(() => console.timeEnd('getInfrastructurePointsTilesToUpdate'));

    onYmapsDebug(() => console.time('infrastructureHTMLPointsLayer update'));
    if (tilesToUpdate.length > 0) {
      infrastructureHTMLPointsLayerRef.current?.drawPins();
    }
    onYmapsDebug(() => console.timeEnd('infrastructureHTMLPointsLayer update'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, htmlInfrastructureFeatures, zoom, bounds]);

  React.useEffect(() => {
    if (!enabled || isWebGlRendering) {
      return;
    }

    if (zoom <= minZoom) {
      infrastructureHTMLPointsLayerRef.current?.clear();
    }

    if ((zoom > minZoom && prevZoomRef.current <= minZoom) || zoom - prevZoomRef.current < 0) {
      infrastructureHTMLPointsLayerRef.current?.drawPins();
    }

    if (
      (zoom >= largePinsZoom && prevZoomRef.current < largePinsZoom) ||
      (zoom < largePinsZoom && prevZoomRef.current >= largePinsZoom)
    ) {
      infrastructureHTMLPointsLayerRef.current?.updatePins();
    }

    prevZoomRef.current = zoom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, largePinsZoom, minZoom, zoom]);

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!mapBounds.bounds || !activeType || [EAsyncStatus.Loading, EAsyncStatus.Initial].includes(status)) {
      return;
    }

    const featuresInBbox = rbushRef.current.search({
      minX: mapBounds.bounds[0][0],
      minY: mapBounds.bounds[0][1],
      maxX: mapBounds.bounds[1][0],
      maxY: mapBounds.bounds[1][1],
    });

    dispatch(setVisibleFeaturesCount({ count: featuresInBbox.length, id: activeType.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mapBounds, dispatch, activeFeaturesCount, status]);
}
